import { useState, useEffect } from 'react';
import { Pagination } from '../networkRequest/api';
import { Logger } from '../utils/logger';

interface PaginatedResponse<T> {
  data?: T[];
  pagination?: Pagination;
  error?: any
}

interface UseBatchedPaginationOptions<TParams = {}> {
  limit?: number;
  enabled?: boolean;
  params?: TParams; // accept additional parameters needed by individual request functions
}

// generic request function
type RequestFunction<T, TParams = {}> = (params: { limit: number; offset: number } & TParams) => Promise<PaginatedResponse<T>>;


interface UseBatchedPaginationResult<T> {
  data: T[];
  loading: boolean;
  error: string;
  refetch: () => void;
  updateItem: (item: T) => void;
  setError: (error: string) => void;
}

const useBatchedPagination = <T, TParams = {}>(
  accessToken: string,
  requestFn: RequestFunction<T, TParams>,
  options: UseBatchedPaginationOptions = {}
): UseBatchedPaginationResult<T> => {
  const { limit = 1000, enabled = true, params } = options;

  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAllData = async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const allRecords: T[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const result = await requestFn({
          accessToken,
          limit,
          offset,
          ...params
        } as { accessToken: string; limit: number; offset: number } & TParams);

        if (!result.data || !result.pagination) {
          throw new Error(result.error)
        }

        // Add the current batch to our collection
        allRecords.push(...result.data);

        // Check if we've fetched all records
        const totalFetched = offset + result.data.length;
        hasMore = totalFetched < result.pagination.total_count;

        // Update offset for next request
        offset += limit;

      }

      setData(allRecords);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      Logger.error('Error fetching batched data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [enabled, JSON.stringify(params)]); // Re-run when enabled or params change

  const refetch = () => {
    fetchAllData();
  };

  const updateItem = (updatedItem: any) => {
    setData(prevData =>
      prevData.map(item =>
        (item as any).id === updatedItem?.id ? updatedItem : item
      )
    );
  };

  return { data, loading, error: error ?? '', setError, refetch, updateItem };
};

export default useBatchedPagination;